/* Prior disclosures and their reconciliation. Every colour is a token (ADR-0007, stylelint). */

/* Which filing: a strip of tabs on the left, like every place's second level - not a row of bordered cards.
   The regime is the tab and the company is its small second line. */
.picker {
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
  border-bottom: 1px solid var(--line);
}

.pick {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1px;
  padding: 6px 12px;
  border: 0;
  border-bottom: 2px solid transparent;
  background: none;
  color: var(--ink-2);
  font-size: var(--t-dense);
  cursor: pointer;
}

.pick:hover {
  color: var(--ink);
}

.pickOn {
  border-bottom-color: var(--accent);
  color: var(--accent);
  font-weight: 650;
}

.pickEntity {
  font-size: var(--t-min);
  font-weight: 400;
  color: var(--muted);
}

/* The filing's prior-disclosure counts: one card, one line of label and figure pairs. */
.facts {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 32px;
  margin: 0;
  padding: 10px 14px;
  border: 1px solid var(--line);
  border-radius: var(--radius);
  background: var(--panel);
}

.fact {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: var(--target-min);
}

.fact dt {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--muted);
  font-size: var(--t-min);
  font-weight: 600;
}

.fact dd {
  margin: 0;
  color: var(--ink);
  font-weight: 650;
}

/* The short line beside a figure. */
.tileNote {
  color: var(--muted);
  font-size: var(--t-min);
}

/* The consequence, stated above the list rather than inferred from it. */
.verdict {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 6px 10px;
  margin: 0;
  padding: 10px 12px;
  border-radius: var(--radius);
  border: 1px solid var(--line-2);
  font-size: var(--t-dense);
  line-height: 1.45;
  color: var(--ink);
}

.verdict > span:not([data-tone]) {
  flex: 1 1 32ch;
  min-width: 0;
}

/* The tint is the second signal; the pill says it in words and with an icon. */
.verdictHeld {
  background: var(--crit-w);
  border-color: var(--crit-line);
}

.verdictClear {
  background: var(--ok-w);
  border-color: var(--ok-line);
}

/* ---------------------------------------------------------------- the page's own layout */

.stack {
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: var(--gap);
  min-width: 0;
}

.columns {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--gap);
  /* The two panels of a row share a top and a bottom (alignment audit, 2026-11-03). */
  align-items: stretch;
  min-width: 0;
}

@media (width <= 1100px) {
  .columns {
    grid-template-columns: minmax(0, 1fr);
  }
}

.differences,
.disclosedList {
  list-style: none;
  margin: 0;
  padding: 0;
}

.difference {
  padding: 9px 0 9px 9px;
  border-top: 1px solid var(--line);
  border-left: 2px solid var(--line-2);
}

.difference:first-child {
  border-top: 0;
}

/* Explained and unexplained are not the same state, so they do not look the same. One holds a
   filing and the other does not, which is the only distinction on this panel that matters. */
.unexplained {
  border-left-color: var(--warn);
}

.explained {
  border-left-color: var(--ok-line);
}

.differenceHead {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 8px;
}

.differenceKey {
  font-size: var(--t-label);
  font-weight: 600;
  color: var(--ink);
}

.differenceState {
  font-size: var(--t-min);
  color: var(--muted);
  white-space: nowrap;
}

.unexplained .differenceState {
  color: var(--warn);
  font-weight: 600;
}

.sides {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-top: 6px;
}

.side {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}

.sideLabel {
  font-size: var(--t-min);
  color: var(--muted);
}

.sideValue {
  font-size: var(--t-body);
  color: var(--ink);
}

.sideLink {
  color: var(--accent);
  text-decoration: none;
  border-bottom: 1px solid var(--accent-line);
}

.sideLink:hover {
  color: var(--accent-h);
}

.delta {
  font-weight: 600;
}

.percent {
  margin-left: 6px;
  font-size: var(--t-min);
  font-weight: 400;
  color: var(--muted);
}

.explanation {
  margin-top: 7px;
  padding: 7px 9px;
  border-radius: 6px;
  background: var(--panel-2);
}

.explanationReason {
  margin: 0;
  font-size: var(--t-min);
  color: var(--ink-2);
  line-height: 1.45;
}

.explanationValues {
  margin: 4px 0 0;
  font-size: var(--t-min);
  color: var(--muted);
  line-height: 1.4;
}

.resolve {
  margin-top: 7px;
}

.resolveLabel {
  display: block;
  font-size: var(--t-min);
  color: var(--ink-2);
  margin-bottom: 3px;
}

.resolveInput {
  width: 100%;
  box-sizing: border-box;
  padding: 5px 7px;
  border: 1px solid var(--line-2);
  border-radius: 5px;
  background: var(--panel);
  color: var(--ink);
  font-family: var(--font);
  font-size: var(--t-min);
  resize: vertical;
}

.resolveInput:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: 1px;
}

.resolveRow,
.formRow {
  display: flex;
  gap: 9px;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 6px;
}

.resolveNote {
  font-size: var(--t-min);
  color: var(--muted);
  line-height: 1.4;
  flex: 1;
  min-width: 0;
}

.resolveError {
  font-size: var(--t-min);
  color: var(--crit);
  line-height: 1.4;
  flex: 1;
  min-width: 0;
}

/* ---------------------------------------------------------------- what was disclosed */

.disclosed {
  padding: 8px 0;
  border-top: 1px solid var(--line);
}

.disclosed:first-child {
  border-top: 0;
}

.disclosedHead {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 8px;
}

.disclosedWhat {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}

.disclosedKey {
  font-size: var(--t-label);
  font-weight: 600;
  color: var(--ink);
}

.disclosedWhen {
  font-size: var(--t-min);
  color: var(--muted);
}

.disclosedValue {
  font-size: var(--t-body);
  color: var(--ink);
  white-space: nowrap;
}

.disclosedMeta {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 4px;
}

.chip {
  padding: 1px 6px;
  border-radius: 4px;
  border: 1px solid var(--line-2);
  font-size: var(--t-min);
}

.chipOn {
  background: var(--accent-w);
  border-color: var(--accent-line);
  color: var(--accent);
}

.chipOff {
  background: var(--panel-2);
  color: var(--muted);
}

.toggle {
  padding: 0;
  border: 0;
  background: none;
  color: var(--accent);
  font-family: var(--font);
  font-size: var(--t-min);
  text-decoration: underline;
  cursor: pointer;
}

.toggle:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: 1px;
}

/* ------------------------------------------------- the receipt, which is not a calculated one */

.receipt {
  margin-top: 7px;
  padding: 8px 9px;
  border-radius: 6px;
  border: 1px solid var(--info-line);
  background: var(--info-w);
}

/* **First, before any figure.** The one thing a reader must not do is read this as something
   EmitIQ produced, so the label is not a footnote. */
.notCalculated {
  margin: 0;
  font-size: var(--t-min);
  font-weight: 600;
  color: var(--info);
  line-height: 1.45;
}

.provenance {
  margin: 5px 0 0;
  font-size: var(--t-min);
  color: var(--ink-2);
  line-height: 1.45;
}

.receiptFacts {
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
  margin: 7px 0 0;
}

.receiptFacts dt {
  font-size: var(--t-min);
  color: var(--muted);
}

.receiptFacts dd {
  margin: 1px 0 0;
  font-size: var(--t-min);
  color: var(--ink);
}

.narrative {
  margin: 7px 0 0;
  padding-left: 8px;
  border-left: 2px solid var(--info-line);
  font-size: var(--t-min);
  color: var(--ink-2);
  line-height: 1.45;
}

.restated {
  margin: 7px 0 0;
  font-size: var(--t-min);
  font-weight: 600;
  color: var(--warn);
  line-height: 1.45;
}

/* ------------------------------------------------------------------------- recording one */

.form {
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.formGrid {
  display: flex;
  gap: 9px;
  flex-wrap: wrap;
}

.wide {
  width: 100%;
}
